import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import "./viewTargets.css";
import { hasAnyPermission } from "../../permissions";

// ---------------------------------------------------------------------------
// Seasonal Rates: hover range
// Hovering a rate shows the range it can sit in: 15% below to 10% above it.
// Example: 1114  ->  947 to 1225.
// ---------------------------------------------------------------------------
const RATE_RANGE_DOWN_PERCENT = 15;
const RATE_RANGE_UP_PERCENT = 10;

// Works from the number that is DISPLAYED in the table (whole rupees), so the
// tooltip always agrees with what the user sees and can check by hand.
// Returns null when there is no rate to show (the table shows "-").
const getRateRange = (value) => {
  if (!value || !Number.isFinite(Number(value))) return null;
  const base = Number(Number(value).toFixed(0));
  return {
    base,
    low: Math.round((base * (100 - RATE_RANGE_DOWN_PERCENT)) / 100),
    high: Math.round((base * (100 + RATE_RANGE_UP_PERCENT)) / 100),
  };
};

// The little box that follows the cursor. It is drawn on the page itself (not
// inside the table) so the table's scrolling never cuts it off, and it moves
// by itself, so the page does not re-render on every mouse movement.
function RateTip({ tip }) {
  const ref = useRef(null);

  useEffect(() => {
    const place = (x, y) => {
      const el = ref.current;
      if (!el) return;
      const gap = 14;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      // stay inside the window: flip to the other side of the cursor near an edge
      const left = x + gap + w > window.innerWidth ? Math.max(4, x - gap - w) : x + gap;
      const top = y + gap + h > window.innerHeight ? Math.max(4, y - gap - h) : y + gap;
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.visibility = "visible";
    };
    place(tip.x, tip.y);
    const onMove = (e) => place(e.clientX, e.clientY);
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [tip.x, tip.y, tip.base]);

  return createPortal(
    <div ref={ref} className="vt-rate-tip" role="tooltip" style={{ visibility: "hidden" }}>
      <div className="vt-rate-tip-range">
        ₹ {tip.low} – ₹ {tip.high}
      </div>
      <div className="vt-rate-tip-note">
        ₹ {tip.base} &minus;{RATE_RANGE_DOWN_PERCENT}% to +{RATE_RANGE_UP_PERCENT}%
      </div>
    </div>,
    document.body
  );
}

function ViewTargets() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Seasonal Rates hover range (see RateTip above)
  const [rateTip, setRateTip] = useState(null);

  // Permission #6 (READ PROPERTY-WISE TARGETS & WRITE REVENUE CALCULATOR)
  // is the only granular permission that grants edit here - #5 (read
  // only) does not. This page previously had NO gating at all on the
  // pencil edit button; this is the fix for that.
  const loggedInRole = localStorage.getItem("loggedInRole");

  // Super Admin/Admin/OTA Manager retain full edit access, matching how
  // this page worked before granular permissions existed — only OTA
  // Executive is meant to be restricted here without an explicit grant.
  const canEditTargets =
    ["SUPER ADMIN", "ADMIN", "OTA MANAGER"].includes(loggedInRole) ||
    hasAnyPermission(["read_targets_write_revenue_calc"]);
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

// EDITABLE WEEKLY START
const [weekInputsByMonth, setWeekInputsByMonth] = useState({});
const ymKey = (year, monthIdx) =>
  `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

const getDefaultWeekInputs = () => ({
  week1: "1",
  week2: "1",
  week3: "1",
  week4: "1",
});

const getMonthWeekInputs = (year, monthIdx) => {
  const key = ymKey(year, monthIdx);
  return weekInputsByMonth[key] || getDefaultWeekInputs();
};

const updateWeekInput = (year, monthIdx, key, value) => {
  const monthKey = ymKey(year, monthIdx);

  setWeekInputsByMonth((prev) => ({
    ...prev,
    [monthKey]: {
      ...getDefaultWeekInputs(),
      ...(prev[monthKey] || {}),
      [key]: value,
    },
  }));
};
  // ENDS HERE
  
  // Editable states (full form)
  const [propertyName, setPropertyName] = useState("");
  const [annualTarget, setAnnualTarget] = useState("");
  const [occupancyPercentage, setOccupancyPercentage] = useState("");
  const [numRooms, setNumRooms] = useState("");
  const [numCategories, setNumCategories] = useState("");

  const [categories, setCategories] = useState([]); // [{name, rooms, percentage}]
  const [lowSeasons, setLowSeasons] = useState([]);
  const [midSeasons, setMidSeasons] = useState([]);
  const [highSeasons, setHighSeasons] = useState([]);
  const [monthOccupancies, setMonthOccupancies] = useState({});
  const [assignedTo, setAssignedTo] = useState("");

  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];

// CALENDAR VARS ADDED HERE ON 26 FEB
  // ---- Calendar (Live) ----
const today = new Date();
const [calYear, setCalYear] = useState(today.getFullYear());
const [calMonth, setCalMonth] = useState(today.getMonth()); // 0 = Jan
const [selectedDate, setSelectedDate] = useState(null);

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const getDaysInMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate();
const getFirstDayOfMonth = (year, monthIdx) => new Date(year, monthIdx, 1).getDay(); // 0=Sun

const goPrevMonth = () => {
  setCalMonth((prev) => {
    if (prev === 0) {
      setCalYear((y) => y - 1);
      return 11;
    }
    return prev - 1;
  });
};

const goNextMonth = () => {
  setCalMonth((prev) => {
    if (prev === 11) {
      setCalYear((y) => y + 1);
      return 0;
    }
    return prev + 1;
  });
};

const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

  const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Weighted daily split using WEEK_VAR (sum exactly equals monthTarget)
const WEEK_VAR = {
  0: 0.9,   // Sun
  1: 0.7,   // Mon
  2: 0.7,   // Tue
  3: 0.77,  // Wed
  4: 0.9,   // Thu
  5: 1.35,  // Fri
  6: 1.68,  // Sat
};

const toNum = (v, fallback = 1) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// weekIndex: 0=week1 (days 1-7), 1=week2 (8-14), 2=week3 (15-21), 3=week4 (22-end)
const getWeekIndexByDay = (day) => Math.min(3, Math.floor((day - 1) / 7));

  // Per-day multipliers (decimal allowed)
const [dayInputs, setDayInputs] = useState({}); // { "YYYY-MM-DD": "1.25", ... }

const updateDayInput = (iso, value) => {
  if (value === "") {
    setDayInputs((prev) => {
      const copy = { ...prev };
      delete copy[iso];
      return copy;
    });
    return;
  }
  // optional safety (no negatives)
  if (parseFloat(value) < 0) return;

  setDayInputs((prev) => ({ ...prev, [iso]: value }));
};

const toFloatOr0 = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};


  
const getWeekRequiredSum = (year, monthIdx, weekIndex) => {
  if (weekIndex < 3) return 7;
  const days = getDaysInMonth(year, monthIdx);
  return Math.max(0, days - 21);
};

// compute week sums for CURRENT month in calendar
const weekSums = useMemo(() => {
  const sums = [0, 0, 0, 0];
  const days = getDaysInMonth(calYear, calMonth);

  for (let day = 1; day <= days; day++) {
    const iso = toISODate(new Date(calYear, calMonth, day));
    const wi = getWeekIndexByDay(day);
    sums[wi] += toFloatOr0(dayInputs[iso]);
  }
  return sums;
}, [calYear, calMonth, dayInputs]);

const weekDiffs = useMemo(() => {
  return [0, 1, 2, 3].map((wi) => {
    const req = getWeekRequiredSum(calYear, calMonth, wi);
    const sum = weekSums[wi] || 0;
    return { wi, req, sum, diff: +(req - sum).toFixed(2) };
  });
}, [calYear, calMonth, weekSums]);
  
const buildDailyTargetsForMonthWithWeeks = (year, monthIdx, monthTarget, weekInputs) => {
  const daysInThisMonth = getDaysInMonth(year, monthIdx);
  const roundedMonthTarget = Math.round(monthTarget || 0);
  if (!daysInThisMonth || roundedMonthTarget <= 0) return {};

  const multipliers = [
    toNum(weekInputs?.week1, 1),
    toNum(weekInputs?.week2, 1),
    toNum(weekInputs?.week3, 1),
    toNum(weekInputs?.week4, 1),
  ];

  const sumMul = multipliers.reduce((a, b) => a + b, 0) || 4;

  const baseWeekly = roundedMonthTarget / sumMul;
  const weeklyTargets = multipliers.map((m) => baseWeekly * m);

  const weeks = [[], [], [], []];
  const weekWeightSum = [0, 0, 0, 0];

  for (let day = 1; day <= daysInThisMonth; day++) {
    const d = new Date(year, monthIdx, day);
    const iso = toISODate(d);
    const w = WEEK_VAR[d.getDay()] ?? 1;

    const wi = getWeekIndexByDay(day);
    weeks[wi].push({ iso, w });
    weekWeightSum[wi] += w;
  }

  const result = {};
  let monthRunningSum = 0;

  for (let wi = 0; wi < 4; wi++) {
    const items = weeks[wi];
    if (!items.length) continue;

    const weekTargetRounded = Math.round(weeklyTargets[wi]);
    const totalW = weekWeightSum[wi] || 1;

    let weekRunning = 0;

    for (let i = 0; i < items.length; i++) {
      const { iso, w } = items[i];

      if (i < items.length - 1) {
        const val = Math.round((weekTargetRounded * w) / totalW);
        result[iso] = val;
        weekRunning += val;
      } else {
        const remainder = weekTargetRounded - weekRunning;
        result[iso] = remainder;
        weekRunning += remainder;
      }
    }

    monthRunningSum += weekRunning;
  }

  const lastISO = toISODate(new Date(year, monthIdx, daysInThisMonth));
  const diff = roundedMonthTarget - monthRunningSum;
  if (diff !== 0 && result[lastISO] != null) {
    result[lastISO] = (result[lastISO] || 0) + diff;
  }

  return result;
};
  
  const assignOptions = ["Narendar", "Arun", "Anshika", "Vipin", "Gopal"];

  const fetchDoc = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`/api/revenue-forecast/${id}`);

      const doc = await res.json();

      if (!res.ok) {
        setError(doc?.message || "Failed to fetch targets");
        setData(null);
        return;
      }

      setData(doc);

      // Prepare edit state defaults (from DB)
      const cats =
        doc?.computed?.categoriesWithRates?.map((c) => ({
          name: c?.name || "",
          rooms: c?.rooms || "",
          percentage: c?.percentage || "",
        })) || [];

      setPropertyName(doc?.propertyName || "");
      setAnnualTarget(doc?.annualTarget || "");
      setOccupancyPercentage(doc?.occupancyPercentage || "");
      setNumRooms(doc?.numRooms || "");
      setNumCategories(doc?.numCategories || String(cats.length || ""));
      setCategories(cats);

      setLowSeasons(doc?.seasons?.lowSeasons || []);
      setMidSeasons(doc?.seasons?.midSeasons || []);
      setHighSeasons(doc?.seasons?.highSeasons || []);

      setMonthOccupancies(doc?.monthOccupancies || {});
      setAssignedTo(doc?.assignedTo || "");
      setCalYear(doc?.computed?.calYear || today.getFullYear());
      setCalMonth(
  Number.isInteger(doc?.computed?.calMonth)
    ? doc.computed.calMonth
    : today.getMonth()
);
      // Load week inputs from DB
const dbWeekInputsByMonth =
  doc?.weekInputsByMonth ||
  doc?.raw?.weekInputsByMonth ||
  {};

setWeekInputsByMonth(dbWeekInputsByMonth);

      const dbDayInputs =
      doc?.dayInputs ||
      doc?.computed?.dayInputs ||
      doc?.raw?.dayInputs ||
      {};

    setDayInputs(dbDayInputs);
      
    } catch (err) {
      console.error(err);
      setError("Server not reachable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoc();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------------- Helpers same as RevenueForecastCalc ----------------
  const isMonthInOtherSeason = (month, currentSeason) => {
    if (currentSeason !== "low" && lowSeasons.includes(month)) return true;
    if (currentSeason !== "mid" && midSeasons.includes(month)) return true;
    if (currentSeason !== "high" && highSeasons.includes(month)) return true;
    return false;
  };

  const handleMultiSelectChange = (e, setter) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setter(selected);
  };

  const getSeasonForMonth = (m) => {
    if (highSeasons.includes(m)) return "high";
    if (midSeasons.includes(m)) return "mid";
    if (lowSeasons.includes(m)) return "low";
    return null;
  };

  const getOccupancyOptionsForMonth = (month) => {
    const season = getSeasonForMonth(month);
    if (season === "low") return [30, 35, 40, 45, 50];
    if (season === "mid") return [50, 55, 60, 65, 70];
    if (season === "high") return [70, 75, 80, 85, 90];
    return [];
  };
  
  const handleMonthOccupancyChange = (month, value) => {
    setMonthOccupancies((prev) => ({ ...prev, [month]: value }));
  };

  const handleNumCategoriesChange = (value) => {
    const count = parseInt(value, 10) || 0;
    setNumCategories(value);

    setCategories((prev) => {
      const next = [];
      for (let i = 0; i < count; i++) {
        next.push(prev[i] || { name: "", rooms: "", percentage: "" });
      }
      return next;
    });
  };

  const updateCategory = (index, field, value) => {
    setCategories((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // ---------------- Live Computation (same logic) ----------------
  const computedLive = useMemo(() => {
    const annualRevenueNum = parseFloat(annualTarget) || 0;
    const occNum = parseFloat(occupancyPercentage) || 0;

    let totalDesired = 0;
    if (annualRevenueNum > 0 && occNum > 0) {
      totalDesired = annualRevenueNum / (occNum / 100);
    }

    const expectedDailyTarget = totalDesired > 0 ? totalDesired / 12 / 30 : 0;

    // Build categoriesWithRates
    const categoriesWithRates = (categories || []).map((cat) => {
      const catPerc = parseFloat(cat.percentage) || 0;
      const catRooms = parseFloat(cat.rooms) || 0;

      const revIntoCategory =
        catPerc > 0 ? expectedDailyTarget * (catPerc / 100) : 0;
      const perDayPerRoom = catRooms > 0 ? revIntoCategory / catRooms : 0;

      return {
        name: cat.name,
        rooms: cat.rooms,
        percentage: cat.percentage,
        rates: {
          low: { weekday: perDayPerRoom * 0.7, weekend: perDayPerRoom * 0.8 },
          mid: { weekday: perDayPerRoom * 0.9, weekend: perDayPerRoom },
          high: { weekday: perDayPerRoom * 1.2, weekend: perDayPerRoom * 1.3 },
        },
      };
    });

    // Annual rows
    const annualRows = months.map((m) => {
      const seasonKey = getSeasonForMonth(m);

      const daysInMonth = new Date(calYear, months.indexOf(m) + 1, 0).getDate();

      let dailyRevenue = 0;

      if (seasonKey) {
        categories.forEach((cat) => {
          const catPerc = parseFloat(cat.percentage) || 0;
          const catRooms = parseFloat(cat.rooms) || 0;
          if (!catRooms || !catPerc) return;

          const revIntoCategory = expectedDailyTarget * (catPerc / 100);
          const perDayPerRoom = catRooms > 0 ? revIntoCategory / catRooms : 0;

          let weekdayVal = 0;
          if (seasonKey === "low") weekdayVal = perDayPerRoom * 0.7;
          if (seasonKey === "mid") weekdayVal = perDayPerRoom * 0.9;
          if (seasonKey === "high") weekdayVal = perDayPerRoom * 1.2;

          dailyRevenue += weekdayVal * catRooms;
        });
      }

      const occSelected = monthOccupancies[m] || 0;
      const occFraction = occSelected > 0 ? occSelected / 100 : 0;

      // OLD ADR
     const roomsNum = parseFloat(numRooms) || 0;

const adr =
  dailyRevenue > 0 && roomsNum > 0
    ? Math.round(dailyRevenue / roomsNum)
    : 0;

      const targetRevenue =
        occFraction > 0 && dailyRevenue > 0
          ? dailyRevenue * occFraction * daysInMonth
          : 0;

      return { month: m, daysInMonth, dailyRevenue, occSelected, adr, targetRevenue };
    });

    let totalOcc = 0;
    let occCount = 0;
    let totalRevenue = 0;
    let adrTotal = 0;
    let adrCount = 0;

    annualRows.forEach((row) => {
      if (row.occSelected > 0) {
        totalOcc += row.occSelected;
        occCount++;
      }
      if (row.targetRevenue > 0) totalRevenue += row.targetRevenue;
      if (row.adr > 0) {
        adrTotal += row.adr;
        adrCount++;
      }
    });

    const avgOcc = occCount > 0 ? totalOcc / occCount : 0;
    const avgAdr = adrCount > 0 ? adrTotal / adrCount : 0;

    return {
      submitted: true,
      expectedDailyTarget,
      categoriesWithRates,
      annualRows,
      totalRevenue,
      avgOcc,
      avgAdr,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    annualTarget,
    occupancyPercentage,
    categories,
    lowSeasons,
    midSeasons,
    highSeasons,
    monthOccupancies,
    calYear, 
  ]);

  // ---------------- Display data (view vs edit) ----------------
  const displayComputed = isEditing ? computedLive : (data?.computed || {});
  const seasonalRates = displayComputed?.categoriesWithRates || [];
  const annualRows = displayComputed?.annualRows || [];
  // NEW ON 6 MARCH STARTS HERE
const dayInputsNumber = useMemo(() => {
  return Object.fromEntries(
    Object.entries(dayInputs || {}).map(([k, v]) => [k, parseFloat(v)])
  );
}, [dayInputs]);

  const dailyTargetsByMonth = useMemo(() => {
  const out = {};

  for (let mi = 0; mi < 12; mi++) {
    const mName = monthNames[mi];
    const row = annualRows.find((r) => r.month === mName);
    const monthTarget = row?.targetRevenue || 0;
    const monthKey = ymKey(calYear, mi);
    const monthWeekInputs = weekInputsByMonth[monthKey] || getDefaultWeekInputs();

    out[monthKey] = buildDailyTargetsForMonthWithWeeks(
      calYear,
      mi,
      monthTarget,
      monthWeekInputs
    );
  }

  return out;
}, [annualRows, calYear, weekInputsByMonth]);

  const weeklyTargetsByMonth = useMemo(() => {
  const out = {};

  for (let mi = 0; mi < 12; mi++) {
    const monthKey = ymKey(calYear, mi);
    const mName = monthNames[mi];
    const row = annualRows.find((r) => r.month === mName);
    const monthTarget = Math.round(row?.targetRevenue || 0);

    const weekInputs = weekInputsByMonth[monthKey] || getDefaultWeekInputs();

    const multipliers = [
      toNum(weekInputs?.week1, 1),
      toNum(weekInputs?.week2, 1),
      toNum(weekInputs?.week3, 1),
      toNum(weekInputs?.week4, 1),
    ];

    const sumMul = multipliers.reduce((a, b) => a + b, 0) || 4;
    const baseWeekly = monthTarget / sumMul;

    const rawWeeklyTargets = multipliers.map((m) => baseWeekly * m);
    const roundedWeeklyTargets = rawWeeklyTargets.map((v) => Math.round(v));

    const weeklyTotal = roundedWeeklyTargets.reduce((a, b) => a + b, 0);
    const diff = monthTarget - weeklyTotal;
    roundedWeeklyTargets[3] = (roundedWeeklyTargets[3] || 0) + diff;

    out[monthKey] = {
      month: mName,
      monthTarget,
      weekInputs: {
        week1: toNum(weekInputs?.week1, 1),
        week2: toNum(weekInputs?.week2, 1),
        week3: toNum(weekInputs?.week3, 1),
        week4: toNum(weekInputs?.week4, 1),
      },
      weeklyTargets: {
        week1: roundedWeeklyTargets[0] || 0,
        week2: roundedWeeklyTargets[1] || 0,
        week3: roundedWeeklyTargets[2] || 0,
        week4: roundedWeeklyTargets[3] || 0,
      },
    };
  }

  return out;
}, [annualRows, calYear, weekInputsByMonth]);

  function applyDayMultipliersToDailyTargets(dailyTargetsByMonth, dayInputs) {
  const out = {};

  for (const [ym, dayMap] of Object.entries(dailyTargetsByMonth || {})) {
    const nextMap = {};
    for (const [iso, baseVal] of Object.entries(dayMap || {})) {
      const raw = dayInputs?.[iso];
      const hasInput = raw !== undefined && raw !== "";
      const mult = hasInput ? parseFloat(raw) : 1;
      const safeMult = Number.isFinite(mult) ? mult : 1;
      nextMap[iso] = Math.round((baseVal || 0) * safeMult);
    }
    out[ym] = nextMap;
  }

  return out;
}
  const finalDailyTargetsByMonth = useMemo(() => {
  return applyDayMultipliersToDailyTargets(dailyTargetsByMonth, dayInputsNumber);
}, [dailyTargetsByMonth, dayInputsNumber]);
  // ENDS HERE
  const currentMonthName = monthNames[calMonth];
  const currentMonthRow = annualRows.find((r) => r.month === currentMonthName);
  const monthTarget = currentMonthRow?.targetRevenue || 0;
  const currentWeekInputs = useMemo(() => {
  return getMonthWeekInputs(calYear, calMonth);
}, [calYear, calMonth, weekInputsByMonth]);
  
 const dailyTargets = useMemo(() => {
  return buildDailyTargetsForMonthWithWeeks(
    calYear,
    calMonth,
    monthTarget,
    currentWeekInputs
  );
}, [calYear, calMonth, monthTarget, currentWeekInputs]);
  
  const totalRevenue = displayComputed?.totalRevenue || 0;
  const avgOcc = displayComputed?.avgOcc || 0;
  const avgAdr = displayComputed?.avgAdr || 0;

  const joinedLow = (lowSeasons || []).length ? lowSeasons.join(", ") : "-";
  const joinedMid = (midSeasons || []).length ? midSeasons.join(", ") : "-";
  const joinedHigh = (highSeasons || []).length ? highSeasons.join(", ") : "-";

  const handleEditClick = () => {
    if (!canEditTargets) {
      alert("Access denied. You are not allowed to edit these targets.");
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
    fetchDoc();
  };

  const handleSave = async () => {
    if (!canEditTargets) {
      alert("Access denied. You are not allowed to save these targets.");
      setIsEditing(false);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
  propertyName,
  annualTarget,
  occupancyPercentage,
  numRooms,
  numCategories,
  seasons: { lowSeasons, midSeasons, highSeasons },
  monthOccupancies,
  assignedTo,
  weekInputsByMonth,
  weeklyTargetsByMonth,
  dayInputs: dayInputsNumber,
  dailyTargetsByMonth,
  finalDailyTargetsByMonth,
  computed: {
    ...displayComputed,
    calYear,
    calMonth,
  },
  raw: {
    propertyName,
    annualTarget,
    occupancyPercentage,
    numRooms,
    numCategories,
    seasons: { lowSeasons, midSeasons, highSeasons },
    monthOccupancies,
    assignedTo,
    weekInputsByMonth,
    weeklyTargetsByMonth,
    dayInputs: dayInputsNumber,
    dailyTargetsByMonth,
    finalDailyTargetsByMonth,
    computed: {
      ...displayComputed,
      calYear,
      calMonth,

    },
  },
};

      const res = await fetch(`/api/revenue-forecast/${id}`, {

        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const updated = await res.json();

      if (!res.ok) {
        setError(updated?.message || "Failed to update");
        return;
      }

      setData(updated);
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setError("Server Error. Unable to Save");
    } finally {
      setSaving(false);
    }
  };

  // Hide the hover range when switching between view and edit mode
  useEffect(() => {
    setRateTip(null);
  }, [isEditing]);

  // One rate cell of the Seasonal Rates table: same text as before, plus the hover range
  const renderRateCell = (value) => {
    const range = getRateRange(value);
    if (!range) return <td>-</td>;
    return (
      <td
        className="vt-rate-cell"
        onMouseEnter={(e) => setRateTip({ x: e.clientX, y: e.clientY, ...range })}
        onMouseLeave={() => setRateTip(null)}
      >
        ₹ {range.base}
      </td>
    );
  };

  if (loading) {
    return (
      <div className="vt-body">
        <p className="vt-title">PROPERTY - WISE TARGETS</p>
        <div className="vt-layout">
          <div className="vt-main">
            <div className="vt-card">Loading property targets...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="vt-body">
        <p className="vt-title">PROPERTY - WISE TARGETS</p>
        <div className="vt-layout">
          <div className="vt-main">
            <div className="vt-card" style={{ color: "#b00020", fontWeight: 600 }}>
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vt-body">
      <p className="vt-title">PROPERTY - WISE TARGETS</p>

      <div className="vt-layout">
        {/* LEFT PANEL */}
        <div className="vt-main">
          <div className="vt-card">
            <div className="view-card-head">
              <h3 className="view-subtitle">Input Details</h3>

              {!isEditing && canEditTargets && (
                <button
                  type="button"
                  className="edit-icon-btn"
                  onClick={handleEditClick}
                  aria-label="Edit"
                  title="Edit"
                >
                  ✎
                </button>
              )}
            </div>

            {error && data && <div className="vt-inline-error">{error}</div>}

            {/* EDIT MODE = FULL FORM */}
            {isEditing ? (
              <>
                <label className="vt-label">Property Name</label>
                <input
                  className="vt-input"
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                />

                <label className="vt-label">Annual Target Revenue *</label>
                <input
                  className="vt-input"
                  type="number"
                  step="0.01"
                  min={0}
                  value={annualTarget}
                  onChange={(e) => setAnnualTarget(e.target.value)}
                />

                <label className="vt-label">Total Number of Rooms</label>
                <input
                  className="vt-input"
                  type="number"
                  step="0.01"
                  min={0}
                  value={numRooms}
                  onChange={(e) => setNumRooms(e.target.value)}
                />

                <label className="vt-label">Number of Room Categories *</label>
                <input
                  className="vt-input"
                  type="number"
                  step="0.01"
                  min={0}
                  max={10}
                  value={numCategories}
                  onChange={(e) => handleNumCategoriesChange(e.target.value)}
                />

                <label className="vt-label">Overall Occupancy % *</label>
                <input
                  className="vt-input"
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={occupancyPercentage}
                  onChange={(e) => setOccupancyPercentage(e.target.value)}
                />

                {/* CATEGORY TABLE (editable) */}
                <h4 className="view-mini-title">Room Category Distribution</h4>
                <div className="vt-table-wrap">
                  <table className="view-table">
                    <thead>
                      <tr>
                        <th>Category Name</th>
                        <th>No. of Rooms</th>
                        <th>Revenue %</th>
                        <th>Average Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat, idx) => {
  // Mid Weekend Rate (Average Rate)
  const avgRate =
    displayComputed?.categoriesWithRates?.[idx]?.rates?.mid?.weekend || 0;

  return (
    <tr key={idx}>
      <td>
        <input
          className="vt-input"
          value={cat.name}
          onChange={(e) =>
            updateCategory(idx, "name", e.target.value)
          }
        />
      </td>

      <td>
        <input
          className="vt-input"
          type="number"
          step="0.01"
          min={0}
          value={cat.rooms}
          onChange={(e) =>
            updateCategory(idx, "rooms", e.target.value)
          }
        />
      </td>

      <td>
        <input
          className="vt-input"
          type="number"
          step="0.01"
          min={0}
          max={100}
          value={cat.percentage}
          onChange={(e) =>
            updateCategory(idx, "percentage", e.target.value)
          }
        />
      </td>

      {/* Average Rate Column */}
      <td>
        {avgRate > 0 ? `₹ ${avgRate.toFixed(0)}` : "-"}
      </td>
    </tr>
  );
})}

                      {categories.length === 0 && (
                        <tr>
                          <td colSpan="4" style={{ textAlign: "center" }}>
                            Add categories by increasing "Number of Room Categories"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* SEASONAL TRENDS (editable) */}

              
                {/* WEEK INPUTS TABLE (View + Edit) */}
<h4 className="view-mini-title" style={{ marginTop: 14 }}>Weekly Variables</h4>

<div className="vt-table-wrap">
  <table className="view-table" style={{ width: "100%" }}>
    <thead>
      <tr>
        <th style={{ textAlign: "center" }}>Week 1</th>
        <th style={{ textAlign: "center" }}>Week 2</th>
        <th style={{ textAlign: "center" }}>Week 3</th>
        <th style={{ textAlign: "center" }}>Week 4</th>
      </tr>
    </thead>

    <tbody>
      <tr>
        {["week1", "week2", "week3", "week4"].map((wk) => (
          <td key={wk}>
            {isEditing ? (
              <input
                className="vt-input"
                type="number"
                step="0.01"
                placeholder="Enter"
               value={currentWeekInputs[wk]}
               onChange={(e) => updateWeekInput(calYear, calMonth, wk, e.target.value)}
              />
            ) : (
              <div style={{ textAlign: "center", fontWeight: 600 }}>
               {currentWeekInputs[wk] !== "" && currentWeekInputs[wk] !== null
  ? currentWeekInputs[wk]
  : "-"}
              </div>
            )}
          </td>
        ))}
      </tr>
    </tbody>
  </table>
</div>
                {/* WEEK CALENDAR ENDS HERE  */}
                <h4 className="view-mini-title">Seasonal Trends</h4>

                <label className="vt-label">Low Seasons *</label>
                <select
                  multiple
                  className="vt-select"
                  value={lowSeasons}
                  onChange={(e) => handleMultiSelectChange(e, setLowSeasons)}
                >
                  {months.map((m) => (
                    <option
                      key={`low-${m}`}
                      value={m}
                      disabled={isMonthInOtherSeason(m, "low")}
                    >
                      {m}
                    </option>
                  ))}
                </select>

                <label className="vt-label">Mid Seasons *</label>
                <select
                  multiple
                  className="vt-select"
                  value={midSeasons}
                  onChange={(e) => handleMultiSelectChange(e, setMidSeasons)}
                >
                  {months.map((m) => (
                    <option
                      key={`mid-${m}`}
                      value={m}
                      disabled={isMonthInOtherSeason(m, "mid")}
                    >
                      {m}
                    </option>
                  ))}
                </select>

                <label className="vt-label">High Seasons *</label>
                <select
                  multiple
                  className="vt-select"
                  value={highSeasons}
                  onChange={(e) => handleMultiSelectChange(e, setHighSeasons)}
                >
                  {months.map((m) => (
                    <option
                      key={`high-${m}`}
                      value={m}
                      disabled={isMonthInOtherSeason(m, "high")}
                    >
                      {m}
                    </option>
                  ))}
                </select>

                {/* MONTH OCCUPANCY (row-grid like you asked) */}
                <h4 className="view-mini-title">Season Occupancy %</h4>

                <div className="vt-occ-box">
                  {months.map((m) => {
                    const options = getOccupancyOptionsForMonth(m);
                    const selected = monthOccupancies[m] || null;
                    const season = getSeasonForMonth(m);

                    return (
                      <div className="vt-occ-row" key={`occ-${m}`}>
                        <div className="vt-occ-month">{m}</div>

                        <div className="vt-occ-options">
                          {season && options.length > 0 ? (
                            options.map((val) => (
                              <label className="vt-occ-option" key={`${m}-${val}`}>
                                <input
                                  type="radio"
                                  name={`occ-${m}`}
                                  value={val}
                                  checked={selected === val}
                                  onChange={() => handleMonthOccupancyChange(m, val)}
                                />
                                {val}%
                              </label>
                            ))
                          ) : (
                            <span style={{ fontSize: 12, color: "#666" }}>
                              Select season above
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ASSIGN TO */}
                <h4 className="view-mini-title">Assign To</h4>
                <div className="vt-table-wrap">
                  <table className="view-table">
                    <thead>
                      <tr>
                        <th>Owner</th>
                        <th>Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignOptions.map((opt) => (
                        <tr key={opt}>
                          <td>{opt}</td>
                          <td>
                            <input
                              type="radio"
                              name="assignTo"
                              value={opt}
                              checked={assignedTo === opt}
                              onChange={(e) => setAssignedTo(e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Buttons */}
                <div className="vt-edit-actions">
                  <button
                    type="button"
                    className="vt-btn vt-btn-ghost"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="vt-btn vt-btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </>
            ) : (
              // VIEW MODE (simple table)
              <div className="vt-table-wrap">
                <table className="view-table">
                  <tbody>
                    <tr><th>Property Name</th><td>{data?.propertyName || "-"}</td></tr>
                    <tr>
                      <th>Annual Target Revenue</th>
                      <td>
                        {data?.annualTarget
                          ? `₹ ${Number(data.annualTarget).toLocaleString("en-IN")}`
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Overall Occupancy</th>
                      <td>{data?.occupancyPercentage ? `${data.occupancyPercentage}%` : "-"}</td>
                    </tr>
                    <tr><th>Total Rooms</th><td>{data?.numRooms || "-"}</td></tr>
                    <tr><th>No. of Categories</th><td>{data?.numCategories || "-"}</td></tr>
                    <tr><th>Low Seasons</th><td>{data?.seasons?.lowSeasons?.join(", ") || "-"}</td></tr>
                    <tr><th>Mid Seasons</th><td>{data?.seasons?.midSeasons?.join(", ") || "-"}</td></tr>
                    <tr><th>High Seasons</th><td>{data?.seasons?.highSeasons?.join(", ") || "-"}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="vt-right-panel">
          <div className="vt-card">
            <h3 className="view-subtitle">Calculated Targets</h3>

            {/* Seasonal Rates */}
            <h4 className="view-mini-title">Seasonal Rates</h4>
            <div className="vt-table-wrap">
              <table className="view-table">
                <thead>
                  <tr>
                    <th>Room Category</th>
                    <th>Rooms</th>
                    <th>Low Weekday</th>
                    <th>Low Weekend</th>
                    <th>Mid Weekday</th>
                    <th>Mid Weekend</th>
                    <th>High Weekday</th>
                    <th>High Weekend</th>
                  </tr>
                </thead>
                <tbody>
                  {seasonalRates.length > 0 ? (
                    seasonalRates.map((cat, idx) => (
                      <tr key={idx}>
                        <td>{cat.name || "-"}</td>
                        <td>{cat.rooms || "-"}</td>
                        {renderRateCell(cat?.rates?.low?.weekday)}
                        {renderRateCell(cat?.rates?.low?.weekend)}
                        {renderRateCell(cat?.rates?.mid?.weekday)}
                        {renderRateCell(cat?.rates?.mid?.weekend)}
                        {renderRateCell(cat?.rates?.high?.weekday)}
                        {renderRateCell(cat?.rates?.high?.weekend)}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: "center" }}>
                        No seasonal rate data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {rateTip && <RateTip tip={rateTip} />}

            {/* Annual Desired Revenue */}
            <h4 className="view-mini-title">Annual Desired Revenue</h4>
            <div className="vt-table-wrap">
              <table className="view-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Occupancy</th>
                    <th>ADR</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {annualRows.length > 0 ? (
                    <>
                      {annualRows.map((r, idx) => (
                        <tr key={idx}>
                          <td>{r.month}</td>
                          <td>{r.occSelected ? `${r.occSelected}%` : "-"}</td>
                          <td>{r.adr ? `₹ ${Number(r.adr).toFixed(0)}` : "-"}</td>
                          <td>{r.targetRevenue ? `₹ ${Number(r.targetRevenue).toFixed(0)}` : "-"}</td>
                        </tr>
                      ))}

                      <tr className="total-row">
                        <td>Total</td>
                        <td>{avgOcc ? `${Number(avgOcc).toFixed(0)}%` : "-"}</td>
                        <td>{avgAdr ? `₹ ${Number(avgAdr).toFixed(0)}` : "-"}</td>
                        <td>{totalRevenue ? `₹ ${Number(totalRevenue).toFixed(0)}` : "-"}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center" }}>
                        No annual revenue rows found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* CALENDAR UI STARTS HERE */}
            {/* WEEK INPUTS TABLE (View + Edit) */}
<h4 className="view-mini-title" style={{ marginTop: 14 }}>Weekly Variables</h4>

<div className="vt-table-wrap">
  <table className="view-table" style={{ width: "100%" }}>
    <thead>
      <tr>
        <th style={{ textAlign: "center" }}>Week 1</th>
        <th style={{ textAlign: "center" }}>Week 2</th>
        <th style={{ textAlign: "center" }}>Week 3</th>
        <th style={{ textAlign: "center" }}>Week 4</th>
      </tr>
    </thead>

    <tbody>
      <tr>
        {["week1", "week2", "week3", "week4"].map((wk) => (
          <td key={wk}>
            {isEditing ? (
              <input
                className="vt-input"
                type="number"
                step="0.01"
                placeholder="Enter"
                value={currentWeekInputs[wk]}
                onChange={(e) => updateWeekInput(calYear, calMonth, wk, e.target.value)}
              />
            ) : (
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                {currentWeekInputs[wk] !== "" && currentWeekInputs[wk] !== null
  ? currentWeekInputs[wk]
  : "-"}
              </div>
            )}
          </td>
        ))}
      </tr>
    </tbody>
  </table>
</div>
<h4 className="view-mini-title" style={{ marginTop: 14 }}>Calendar</h4>

<div className="vt-cal-wrap">
  <div className="live-cal-header">
    <button type="button" className="cal-nav" onClick={goPrevMonth}>
      ←
    </button>

    <div className="cal-title">
      {monthNames[calMonth]} {calYear}
    </div>

    <button type="button" className="cal-nav" onClick={goNextMonth}>
      →
    </button>
  </div>

  <div style={{ marginTop: 8, fontSize: 12 }}>
  {weekDiffs.map(({ wi, req, sum, diff }) => (
    <div key={wi} style={{ marginBottom: 4 }}>
      <b>Week {wi + 1}</b>: Sum = {sum.toFixed(2)} / Required = {req}
      <span
        style={{
          marginLeft: 8,
          color: diff === 0 ? "green" : "red",
          fontWeight: 700,
        }}
      >
        {diff === 0 ? "✅ OK" : `⚠ Remaining: ${diff > 0 ? diff : `-${Math.abs(diff)}`}`}
      </span>
    </div>
  ))}
</div>

  <div className="cal-weekdays">
    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
      <div key={d} className="cal-weekday">{d}</div>
    ))}
  </div>

  <div className="cal-grid">
    {(() => {
      const blanks = getFirstDayOfMonth(calYear, calMonth);
      const days = getDaysInMonth(calYear, calMonth);

      const cells = [];

      for (let i = 0; i < blanks; i++) {
        cells.push(<div key={`b-${i}`} className="cal-cell cal-blank" />);
      }

      for (let day = 1; day <= days; day++) {
  const dateObj = new Date(calYear, calMonth, day);
  const isToday = isSameDay(dateObj, today);
  const isSelected = selectedDate && isSameDay(dateObj, selectedDate);

  const iso = toISODate(dateObj);
  const baseTarget = dailyTargets[iso] || 0;

  const inputStr = dayInputs[iso];        // string or undefined
  const hasInput = inputStr !== undefined;
  const mult = hasInput ? parseFloat(inputStr) : 1;

  const finalTarget =
    monthTarget > 0
      ? hasInput
        ? Math.round(baseTarget * (Number.isFinite(mult) ? mult : 1))
        : Math.round(baseTarget)
      : 0;

  const dayOfWeek = dateObj.getDay();
  const isPeakTravelDay = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;

  cells.push(
    <div
      key={`d-${day}`}
      role="button"
      tabIndex={0}
      className={`cal-cell cal-day ${isPeakTravelDay ? "cal-peak" : ""} ${
        isToday ? "cal-today" : ""
      } ${isSelected ? "cal-selected" : ""}`}
      onClick={() => setSelectedDate(dateObj)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setSelectedDate(dateObj);
      }}
      title={dateObj.toDateString()}
    >
      <div className="cal-daynum">{day}</div>

      <div className="cal-targetrow">
        <div className="cal-daytarget">
          {monthTarget > 0 ? `₹ ${Number(finalTarget).toLocaleString("en-IN")}` : ""}
        </div>

      <input
  className="cal-dayinput"
  type="number"
  step="0.01"
  min="0"
  value={inputStr ?? ""}
  disabled={!isEditing}                 // lock in view mode
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
  onChange={(e) => {
    if (!isEditing) return;             // extra safety
    updateDayInput(iso, e.target.value);
  }}
/>
      </div>
    </div>
  );
}

      return cells;
    })()}
  </div>

  <div className="cal-footer">
    <span>Selected: {selectedDate ? selectedDate.toDateString() : "-"}</span>
  </div>
</div>
            {/* CALENDAR UI ENDS HERE */}
            {isEditing && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#023e00" }}>
                <b>Selected Seasons:</b> Low: {joinedLow} | Mid: {joinedMid} | High: {joinedHigh}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="copyright_footer">
        © 2026 RevX Hospitality. All Rights Reserved. Developed by <span style={{ color: 'green' }}>RevX Hospitality</span>.
      </div>
    </div>
  );
}

export default ViewTargets;
